import React, {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
} from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { SORT_DIRECTIONS, TABLE_SIZES } from '../consts';
import useTableSize from '../hooks/useTableSize';
import { Themeable, IsSorted } from '../types';

export type TableHeaderProps = StylableComponent<HTMLDivElement> &
  Themeable & {
    isResizingColumn?: boolean;
    isResizingAnyColumn?: boolean;
    isPlaceholder?: boolean;
    hasSubHeaders?: boolean;
    canResize?: boolean;
    handleResize?: (event: unknown) => void;
    canSort?: boolean;
    isSorted?: IsSorted;
    toggleSort?: (event: unknown) => void;
    colSpan?: number;
    size?: number | string;
    renderValue?: () => ReactNode;
  };

const preventTextHighlight = (
  e: React.MouseEvent<HTMLDivElement, MouseEvent>,
) => {
  e.preventDefault();
};

const TableHeader = ({
  tableSize,
  size,
  isPlaceholder,
  hasSubHeaders,
  canResize,
  isResizingColumn,
  isResizingAnyColumn,
  handleResize,
  canSort,
  isSorted,
  toggleSort,
  renderValue,
  children,
  style,
  ...props
}: PropsWithChildren<TableHeaderProps>): ReactElement => {
  const tableSizeTheme = useTableSize(tableSize);
  const theme = useTheme();
  const headerTokens = theme.tokens?.component?.table?.header;
  const isPrimaryRow = !isPlaceholder && !hasSubHeaders;
  const rowSlice = isPrimaryRow
    ? headerTokens?.primary
    : headerTokens?.secondary;

  const headerCss = useMemo(
    (): CSSObject => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: isPrimaryRow ? 'flex-start' : 'center',
      position: 'relative' as const,
      width: size,
      flex: `${size} 0 auto`,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid' as const,
      padding: `0 ${tableSizeTheme.cellPadding}px`,
      '.tableHeaderCellSortIcon': {
        marginLeft: 'auto',
        minWidth: 20,
      },
      ...headerTokens?._,
      fontWeight: rowSlice?.['font-weight'],
      borderColor: rowSlice?.['border-color'],
      ...(isPrimaryRow
        ? {}
        : {
            borderRightWidth: rowSlice?.['border-right-width'],
            borderRightStyle: rowSlice?.['border-right-style'],
            borderRightColor: rowSlice?.['border-right-color'],
            '&:last-child': {
              borderRightWidth: 0,
            },
          }),
      ...(canResize
        ? {
            '&:hover': {
              '& > [role="separator"]': headerTokens?.resizable?.['&:hover'],
            },
          }
        : {}),
      ...(canSort
        ? {
            ...headerTokens?.sortable?._,
            '&:hover': {
              ...headerTokens?.sortable?.['&:hover'],
              '& > .tableHeaderCellUnsortedIcon': {
                visibility: 'visible',
              },
              '& > [role="separator"]': headerTokens?.resizable?.['&:hover'],
            },
          }
        : {}),
    }),

    [
      size,
      tableSizeTheme.cellPadding,
      headerTokens,
      isPrimaryRow,
      rowSlice,
      canResize,
      canSort,
    ],
  );

  const resizeIndicatorCss = useMemo(
    (): CSSObject => ({
      zIndex: 1,
      width: 3,
      height: '100%',
      position: 'absolute' as const,
      right: 0,
      ...headerTokens?.resizeIndicator?._,
      ...(isResizingColumn ? headerTokens?.resizeIndicator?.active : {}),
      '&:hover': {
        cursor: 'col-resize',
      },
    }),
    [headerTokens, isResizingColumn],
  );

  return (
    <th
      css={[headerCss, style]}
      onMouseDown={(e) => {
        if (canSort && !isResizingAnyColumn) {
          toggleSort?.(e);
        }
      }}
      {...props}
    >
      {children ||
        (!isPlaceholder && (
          <>
            {renderValue?.()}
            {canSort &&
              (() => {
                switch (isSorted) {
                  case SORT_DIRECTIONS.ASC:
                    return (
                      <ArrowUpIcon
                        size={20}
                        className="tableHeaderCellSortIcon"
                      />
                    );
                  case SORT_DIRECTIONS.DESC:
                    return (
                      <ArrowDownIcon
                        size={20}
                        className="tableHeaderCellSortIcon"
                      />
                    );
                  default:
                    return (
                      <ArrowUpDownIcon
                        className="tableHeaderCellSortIcon tableHeaderCellUnsortedIcon"
                        size={20}
                        style={{
                          visibility: 'hidden',
                        }}
                      />
                    );
                }
              })()}
          </>
        ))}
      {canResize && (
        // Will be fixed in https://jira.autodesk.com/browse/DSGN-1840
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role={'separator'}
          onMouseDown={(e) => {
            e.stopPropagation();
            preventTextHighlight(e);
            handleResize?.(e);
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleResize}
          css={resizeIndicatorCss}
        />
      )}
    </th>
  );
};

TableHeader.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPlaceholder: PropTypes.bool,
  hasSubHeaders: PropTypes.bool,
  canResize: PropTypes.bool,
  isResizingColumn: PropTypes.bool,
  isResizingAnyColumn: PropTypes.bool,
  handleResize: PropTypes.func,
  canSort: PropTypes.bool,
  isSorted: PropTypes.oneOf([...Object.values(SORT_DIRECTIONS), false]),
  toggleSort: PropTypes.func,
  renderValue: PropTypes.func,
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  children: PropTypes.any,
  style: stylePropType,
};

export default TableHeader;
